/**
 * EA Code Guide - Async Analysis API Service
 * Handles EA file analysis with chunked processing
 *
 * Server: FIX Engine (baseURL)
 * Auth: JWT Bearer token
 *
 * Endpoints:
 * - POST   /api/q/ea/analyze/async         - Start async analysis (returns jobId, queuePosition)
 * - GET    /api/q/ea/job/{jobId}           - Get job status and results
 * - DELETE /api/q/ea/job/{jobId}           - Cancel running job
 * - POST   /api/q/ea/validate              - Pre-validate file before upload
 * - GET    /api/q/ea/history               - Get analysis history
 * - GET    /api/q/ea/check-hash/{userId}/{hash} - P9-B: Check for duplicate EA by hash
 * - GET    /api/q/ea/analysis/{analysisId} - Get analysis by ID
 * - GET    /api/q/mql/library/{userId}     - P9-C: Get MQL code library
 *
 * SignalR Hub: wss://[host]/hubs/ea-progress
 * Events: OnProgress, OnComplete, OnError, OnJobCancelled
 */

import axios from 'axios';
import { baseURL } from '../config';

// ═══════════════════════════════════════════════════════════════════════════
// HELPER - Get auth headers
// ═══════════════════════════════════════════════════════════════════════════

const getAuthHeaders = (isMultipart = false) => {
    // QAI auth uses 'qai_token', main app uses 'token' - check both
    const token = localStorage.getItem('qai_token') || localStorage.getItem('token');
    const headers = {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
    };
    if (!isMultipart) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

// ═══════════════════════════════════════════════════════════════════════════
// FILE VALIDATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Validate file before upload (client-side)
 * @param {File} file - The file to validate
 * @returns {{valid: boolean, errors: string[]}}
 */
export const validateFileClient = (file) => {
    const errors = [];
    const MAX_SIZE_KB = 500;
    const ALLOWED_EXTENSIONS = ['.mq5', '.mqh'];

    if (!file) {
        errors.push('No file selected');
        return { valid: false, errors };
    }

    // Check extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) {
        errors.push(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
    }

    // Check size
    const fileSizeKB = file.size / 1024;
    if (fileSizeKB > MAX_SIZE_KB) {
        errors.push(`File too large (${fileSizeKB.toFixed(1)}KB). Maximum: ${MAX_SIZE_KB}KB`);
    }

    return {
        valid: errors.length === 0,
        errors,
        fileInfo: {
            name: file.name,
            size: fileSizeKB.toFixed(1) + 'KB',
            type: fileName.endsWith('.mq5') ? 'MQ5 Expert Advisor' : 'MQH Header File'
        }
    };
};

/**
 * Pre-validate file on server before full upload
 * @param {File} file - The file to validate
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const validateFileServer = async (file) => {
    try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${baseURL}/api/q/ea/validate`, formData, {
            headers: getAuthHeaders(true)
        });

        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to validate EA file:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to validate file'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// ASYNC ANALYSIS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Start async EA analysis
 * @param {object} analysisData - Analysis request data
 * @param {string} analysisData.name - File name
 * @param {string} analysisData.mainSource - Source code content
 * @param {boolean} analysisData.consentGiven - User consent (REQUIRED)
 * @param {Object<string, string>} analysisData.includeFiles - Optional include files
 * @returns {Promise<{success: boolean, data?: {jobId: string, queuePosition: number}, error?: string}>}
 */
export const startAsyncAnalysis = async (analysisData) => {
    try {
        // Validate consent
        if (!analysisData.consentGiven) {
            return {
                success: false,
                error: 'User consent is required to analyze EA code'
            };
        }

        // Validate source
        const mainSource = analysisData.mainSource || analysisData.main_source || '';
        if (!mainSource || mainSource.trim().length === 0) {
            return {
                success: false,
                error: 'EA source code is empty or missing'
            };
        }

        // Build JSON payload (matches backend API contract)
        const payload = {
            name: analysisData.name || analysisData.fileName,
            mainSource: mainSource,
            consentGiven: true
        };

        // Include optional include files
        if (analysisData.includeFiles && Object.keys(analysisData.includeFiles).length > 0) {
            payload.includeFiles = analysisData.includeFiles;
        }

        console.log('[eaAnalysisApi] startAsyncAnalysis payload:', {
            name: payload.name,
            mainSourceLength: payload.mainSource?.length || 0,
            consentGiven: payload.consentGiven
        });

        const response = await axios.post(`${baseURL}/api/q/ea/analyze/async`, payload, {
            headers: getAuthHeaders(false) // JSON, not multipart
        });

        // DEBUG: Log raw response from FIX Engine to diagnose proxy issue
        console.log('[eaAnalysisApi] Raw API response:', JSON.stringify(response.data, null, 2));

        // Extract data - check both wrapped and unwrapped formats
        const data = response.data.data || response.data;

        // Log what we're returning to the hook
        console.log('[eaAnalysisApi] Returning data:', {
            hasStatus: !!data.status,
            status: data.status,
            hasResult: !!data.result,
            jobId: data.jobId
        });

        return {
            success: true,
            data: data
        };
    } catch (error) {
        console.error('Failed to start EA analysis:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to start analysis'
        };
    }
};

/**
 * Get job status and results
 * @param {string} jobId - The job ID to check
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getJobStatus = async (jobId) => {
    try {
        const response = await axios.get(`${baseURL}/api/q/ea/job/${jobId}`, {
            headers: getAuthHeaders()
        });

        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to get job status:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to get job status'
        };
    }
};

/**
 * Cancel a running job
 * @param {string} jobId - The job ID to cancel
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const cancelJob = async (jobId) => {
    try {
        await axios.delete(`${baseURL}/api/q/ea/job/${jobId}`, {
            headers: getAuthHeaders()
        });

        return { success: true };
    } catch (error) {
        console.error('Failed to cancel job:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to cancel job'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// HISTORY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get analysis history for current user
 * Backend identifies user from JWT token, not path param
 * Endpoint: GET /api/q/ea/history?limit=N
 *
 * @param {object} options - Query options
 * @param {number} options.limit - Max records to return (default 10, max 50)
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getAnalysisHistory = async (options = {}) => {
    const { limit = 10 } = options;

    try {
        const params = new URLSearchParams({
            limit: Math.min(limit, 50).toString()
        });

        const response = await axios.get(`${baseURL}/api/q/ea/history?${params}`, {
            headers: getAuthHeaders()
        });

        // Response contains array of analyzed files
        const result = response.data.data || response.data;
        return {
            success: true,
            data: result.history || result || []
        };
    } catch (error) {
        console.error('Failed to get analysis history:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to get history'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// RESULT HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Download analysis results as JSON
 * @param {object} results - The analysis results
 * @param {string} filename - The filename for download
 */
export const downloadResults = (results, filename = 'ea-analysis-results.json') => {
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

/**
 * Parse job status to user-friendly format
 * @param {string} status - Raw status from API
 * @returns {{label: string, color: string, isComplete: boolean, isError: boolean}}
 */
export const parseJobStatus = (status) => {
    const statusMap = {
        'queued': { label: 'Queued', color: 'info', isComplete: false, isError: false },
        'processing': { label: 'Processing', color: 'warning', isComplete: false, isError: false },
        'completed': { label: 'Completed', color: 'success', isComplete: true, isError: false },
        'failed': { label: 'Failed', color: 'error', isComplete: true, isError: true },
        'cancelled': { label: 'Cancelled', color: 'default', isComplete: true, isError: false }
    };

    return statusMap[status?.toLowerCase()] || {
        label: status || 'Unknown',
        color: 'default',
        isComplete: false,
        isError: false
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// P9-B: DUPLICATE DETECTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Check if EA content hash already exists (P9-B)
 * Endpoint: GET /api/q/ea/check-hash/{userId}/{hash}
 *
 * @param {string} userId - User ID
 * @param {string} hash - SHA-256 hash of EA content
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 *
 * Response when exists:
 * {
 *   exists: true,
 *   analysisId: "guid",
 *   eaName: "GoldEA.mq5",
 *   qualityScore: 7.5,
 *   createdAt: "2025-12-18T10:00:00Z",
 *   message: "This EA was already analyzed on 2025-12-18. Use existing?"
 * }
 */
export const checkEAHash = async (userId, hash) => {
    try {
        if (!userId || !hash) {
            return {
                success: false,
                error: 'User ID and hash are required'
            };
        }

        const response = await axios.get(`${baseURL}/api/q/ea/check-hash/${userId}/${hash}`, {
            headers: getAuthHeaders()
        });

        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        // 404 means hash not found - this is a valid "no duplicate" response
        if (error.response?.status === 404) {
            return {
                success: true,
                data: { exists: false }
            };
        }

        console.error('Failed to check EA hash:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to check hash'
        };
    }
};

/**
 * Get existing analysis result by ID
 * Used when user chooses to use existing analysis from duplicate detection
 *
 * @param {string} analysisId - Analysis ID from hash check
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getAnalysisById = async (analysisId) => {
    try {
        if (!analysisId) {
            return {
                success: false,
                error: 'Analysis ID is required'
            };
        }

        const response = await axios.get(`${baseURL}/api/q/ea/analysis/${analysisId}`, {
            headers: getAuthHeaders()
        });

        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to get analysis:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to get analysis'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// P9-C: MQL CODE LIBRARY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get user's MQL code library (P9-C)
 * Endpoint: GET /api/q/mql/library/{userId}?fileType=EA&limit=20
 *
 * @param {string} userId - User ID
 * @param {object} options - Query options
 * @param {string} options.fileType - Filter by type: 'EA' | 'Indicator' | 'Script' | 'All'
 * @param {number} options.limit - Max records (default 20)
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 *
 * Response:
 * {
 *   success: true,
 *   data: [
 *     {
 *       fileId: "guid",
 *       name: "GoldEA.mq5",
 *       fileType: "EA",
 *       serialNo: 1,
 *       lineCount: 450,
 *       qualityScore: 7.5,
 *       latestAnalysisId: "guid",
 *       displayLabel: "EA-001: GoldEA.mq5"
 *     }
 *   ],
 *   grouped: { EA: [...], Indicator: [...] },
 *   byType: { ea: 5, indicator: 2, script: 1 }
 * }
 */
export const getMQLLibrary = async (userId, options = {}) => {
    try {
        if (!userId) {
            return {
                success: false,
                error: 'User ID is required'
            };
        }

        const { fileType = 'All', limit = 20 } = options;

        const params = new URLSearchParams();
        if (fileType && fileType !== 'All') {
            params.append('fileType', fileType);
        }
        params.append('limit', Math.min(limit, 100).toString());

        const url = `${baseURL}/api/q/mql/library/${userId}?${params}`;
        const response = await axios.get(url, {
            headers: getAuthHeaders()
        });

        const result = response.data.data || response.data;

        return {
            success: true,
            data: result.data || [],
            grouped: result.grouped || {},
            byType: result.byType || {}
        };
    } catch (error) {
        console.error('Failed to get MQL library:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to get library',
            data: [],
            grouped: {},
            byType: {}
        };
    }
};

/**
 * Load analysis from library (instant, 0 tokens)
 * Fetches the full analysis result for a library item
 *
 * @param {string} analysisId - Latest analysis ID from library item
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const loadLibraryAnalysis = async (analysisId) => {
    // Reuse getAnalysisById - same endpoint
    return getAnalysisById(analysisId);
};
